import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { colors } from '../../brand-colors';

const brandButtonStyle = {
  background: `linear-gradient(180deg, ${colors.redDark} 0%, ${colors.greyDarkest} 100%)`,
  border: 'none',
  color: colors.greyLightest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
  transition: 'all 0.2s ease',
};

const brandButtonHoverStyle = {
  background: `linear-gradient(180deg, ${colors.red} 0%, ${colors.greyDarker} 100%)`,
};

/**
 * DecisionGate - The choice screen for unified Mind to Method flow
 * User chooses between "I have an IP in mind" (V1 path) or "I want to explore" (V2 path)
 */
function DecisionGate({ sessionId, userName, onSelectPath, loading, error }) {
  const [selectedPath, setSelectedPath] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleContinue = () => {
    if (selectedPath) {
      onSelectPath(selectedPath);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h2 className="text-center mb-3" style={{ color: '#1a365d' }}>
                Hi {userName}, let's discover your IP
              </h2>
              <p className="text-center text-muted mb-5">
                How would you like to approach this? Choose the path that fits you best.
              </p>

              {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

              <Row className="g-4 mb-4">
                {/* Option 1: Have IP in mind */}
                <Col md={6}>
                  <Card
                    className={`h-100 cursor-pointer ${selectedPath === 'have_ip' ? 'border-primary' : ''}`}
                    style={{
                      cursor: 'pointer',
                      borderWidth: selectedPath === 'have_ip' ? '3px' : '1px',
                      backgroundColor: selectedPath === 'have_ip' ? '#f0f7ff' : hoveredCard === 'have_ip' ? '#fafafa' : '#fff',
                      transition: 'all 0.2s ease',
                      minHeight: '280px'
                    }}
                    onClick={() => setSelectedPath('have_ip')}
                    onMouseEnter={() => setHoveredCard('have_ip')}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="text-center mb-3">
                        <span style={{ fontSize: '3rem' }}>🎯</span>
                      </div>
                      <h4 className="text-center mb-3" style={{ color: selectedPath === 'have_ip' ? '#0d6efd' : '#1a365d' }}>
                        I Have an IP in Mind
                      </h4>
                      <p className="text-muted text-center flex-grow-1">
                        You already know what expertise, framework, or methodology you want to extract and document.
                        We'll dive deep to codify it.
                      </p>
                      <div className="mt-auto">
                        <p className="small text-center mb-0" style={{ color: '#718096' }}>
                          Best for: Experts who know their unique approach
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Option 2: Want to explore */}
                <Col md={6}>
                  <Card
                    className={`h-100 cursor-pointer ${selectedPath === 'explore' ? 'border-primary' : ''}`}
                    style={{
                      cursor: 'pointer',
                      borderWidth: selectedPath === 'explore' ? '3px' : '1px',
                      backgroundColor: selectedPath === 'explore' ? '#f0f7ff' : hoveredCard === 'explore' ? '#fafafa' : '#fff',
                      transition: 'all 0.2s ease',
                      minHeight: '280px'
                    }}
                    onClick={() => setSelectedPath('explore')}
                    onMouseEnter={() => setHoveredCard('explore')}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="text-center mb-3">
                        <span style={{ fontSize: '3rem' }}>🔍</span>
                      </div>
                      <h4 className="text-center mb-3" style={{ color: selectedPath === 'explore' ? '#0d6efd' : '#1a365d' }}>
                        I Want to Explore
                      </h4>
                      <p className="text-muted text-center flex-grow-1">
                        You're not sure what IP you have, or want help discovering hidden expertise.
                        We'll help you uncover your valuable assets first.
                      </p>
                      <div className="mt-auto">
                        <p className="small text-center mb-0" style={{ color: '#718096' }}>
                          Best for: Discovering hidden valuable IP
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Visual explanation of paths */}
              <div className="p-3 mb-4" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                {selectedPath === 'have_ip' && (
                  <div className="text-center">
                    <strong>Your Path:</strong> Deep Extraction
                    <p className="mb-0 small text-muted mt-1">
                      We'll ask targeted questions to extract your methodology across 5 layers:
                      Identity, Pain Patterns, Decision Logic, Boundaries, and Language.
                    </p>
                  </div>
                )}
                {selectedPath === 'explore' && (
                  <div className="text-center">
                    <strong>Your Path:</strong> Discovery First
                    <p className="mb-0 small text-muted mt-1">
                      We'll help you identify what IP assets you have across 10 categories,
                      then you can choose one to codify into a complete framework.
                    </p>
                  </div>
                )}
                {!selectedPath && (
                  <p className="text-center text-muted mb-0">
                    Select an option above to continue
                  </p>
                )}
              </div>

              <Button
                size="lg"
                className="w-100"
                onClick={handleContinue}
                disabled={loading || !selectedPath}
                style={brandButtonStyle}
                onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
              >
                {loading ? (
                  <><Spinner size="sm" className="me-2" />Setting up...</>
                ) : (
                  <>Continue with {selectedPath === 'have_ip' ? 'Deep Extraction' : selectedPath === 'explore' ? 'Discovery' : '...'}</>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default DecisionGate;
